import { notification } from 'antd';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { clientsMapperFromApi } from '@/components/App/Clients/helpers/clientsMapper';
import { Button } from '@/components/Shared/ui';
import { formatPhoneNumber } from '@/services/formatter';
import { getUserFullName } from '@/services/helpers/strings';
import { useAppDispatch } from '@/store';
import { getClientByIin } from '@/store/clients.slice';

import type { ClientFormValues } from '../types/client-form';

interface IProps {
  submitRequest: (values: ClientFormValues) => Promise<string>;
  handleNext: () => void;
}

const useClientForm = ({ submitRequest, handleNext }: IProps) => {
  const dispatch = useAppDispatch();

  const methods = useForm<ClientFormValues, unknown>({
    mode: 'all',
    delayError: 200,
    defaultValues: {
      phone: ''
    }
  });
  const values = useWatch({ control: methods.control });

  const [searchClientLoading, setSearchClientLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [clientError, setClientError] = useState('');

  const [clientComments, setClientComments] = useState<string[]>([]);
  const [clientSecurity, setClientSecurity] = useState<{
    statusCode: string | null;
    comment: string | null;
  }>({
    statusCode: null,
    comment: null
  });

  const resetClientComments = () => {
    setClientComments([]);
  };

  useEffect(() => {
    // Сбрасываем ошибку при изменении полей
    if (clientError) {
      setClientError('');
    }
  }, [values]);

  useEffect(() => {
    const abortController = new AbortController();
    (async () => {
      setClientSecurity({
        statusCode: null,
        comment: null
      });
      if (values.iin?.length !== 12) return;

      setSearchClientLoading(true);
      const response = await dispatch(
        getClientByIin({ iin: values.iin, abortSignal: abortController.signal })
      );
      if (getClientByIin.fulfilled.match(response) && response.payload) {
        const client = clientsMapperFromApi(response.payload);

        const newValues: ClientFormValues = {
          iin: response.payload?.client_iin,
          fullName: '',
          phone: '',
          sentWithType: 'SMS'
        };
        const shouldDisable = { fullName: false, phone: false };

        // Запрещаем редактирование полей, если соответствующие данные найдены
        if (client.lastName && client.firstName) {
          newValues.fullName = getUserFullName(client);
          shouldDisable.fullName = newValues.fullName.length !== 0;
        }
        if (client.phoneNumber) {
          newValues.phone = formatPhoneNumber(client.phoneNumber);
          const maskLength = '+7 (___) ___-__-__'.length;
          shouldDisable.phone = newValues.phone.length === maskLength;
        }
        if (client.comments && client.comments.length > 0) {
          setClientComments(client.comments);
        } else {
          setClientComments([]);
        }
        setClientSecurity({
          statusCode: String(client.status?.code),
          comment: client.commentForUser
        });
        if (!(values.fullName || values.phone)) {
          methods.reset(newValues);
        } else {
          notification.info({
            key: values.iin,
            duration: 10,
            title: (
              <p>
                Найден клиент с ИИН <b>{values.iin}</b>
              </p>
            ),
            description: 'Хотите использовать загруженные данные?',
            showProgress: true,
            actions: [
              <Button
                onClick={() => notification.destroy(values.iin)}
                size="small"
                key={'cancel'}
              >
                Нет
              </Button>,
              <Button
                onClick={() => {
                  methods.reset(newValues);
                  notification.destroy(values.iin);
                }}
                size="small"
                variant="filled-red"
                key={'confirm'}
              >
                Да
              </Button>
            ],
            className: 'client-otp-modal-notification'
          });
        }
      } else {
        setClientSecurity({
          statusCode: null,
          comment: null
        });
      }
      setSearchClientLoading(false);
    })();

    return () => {
      abortController.abort();
    };
  }, [values.iin, dispatch]);

  const handleClientFormSubmit = async (values: ClientFormValues) => {
    setSubmitLoading(true);
    const responseError = await submitRequest(values);
    if (!responseError) {
      handleNext();
    } else {
      setClientError(responseError);
    }
    setSubmitLoading(false);
  };

  return {
    clientComments,
    clientError,
    clientFormLoading: submitLoading,
    clientFormMethods: methods,
    clientSecurity,
    handleClientFormSubmit,
    resetClientComments,
    searchClientLoading
  };
};

export default useClientForm;
